/**
 * Elevation Service - Tile-based elevation lookup using Crew Terrain API
 */

import { crewTerrainCarpetList } from './types/api.generated';
import type { crewTerrainCarpetListResponse, CrewTerrainCarpetList200 } from './types/api.generated';

const TILE_SIZE_DEGREES = 0.01; // 0.01 degrees per tile (~1.1 km at equator)
const TILE_VALUE_SPACING_DEGREES = 1 / 3600; // 1 arc-second (~30 meters)

interface TileBounds {
  swLat: number;
  swLon: number;
  neLat: number;
  neLon: number;
}

interface CachedTile {
  bounds: TileBounds;
  carpet: number[][];
  gridSizeLat: number;
  gridSizeLon: number;
  timestamp: number;
}

type TileKey = string;

class ElevationService {
  private tileCache: Map<TileKey, CachedTile> = new Map();
  private pendingRequests: Map<TileKey, Promise<CachedTile>> = new Map();
  private requestCache: Map<string, CachedTile> = new Map(); // Cache by points string
  private pendingApiRequests: Map<string, Promise<CachedTile>> = new Map(); // Track pending API requests by points
  private readonly CACHE_EXPIRY_MS = 24 * 60 * 60 * 1000; // 24 hours

  private static long2tileX(lon: number): number {
    return Math.floor((lon + 180.0) / TILE_SIZE_DEGREES);
  }

  private static lat2tileY(lat: number): number {
    return Math.floor((lat + 90.0) / TILE_SIZE_DEGREES);
  }

  private static tileToBounds(x: number, y: number): TileBounds {
    const swLat = y * TILE_SIZE_DEGREES - 90.0;
    const swLon = x * TILE_SIZE_DEGREES - 180.0;
    const neLat = (y + 1) * TILE_SIZE_DEGREES - 90.0;
    const neLon = (x + 1) * TILE_SIZE_DEGREES - 180.0;
    return { swLat, swLon, neLat, neLon };
  }

  private static getTileKey(lat: number, lon: number): TileKey {
    const x = ElevationService.long2tileX(lon);
    const y = ElevationService.lat2tileY(lat);
    return `${x}_${y}`;
  }

  private static getTileKeyFromCoords(x: number, y: number): TileKey {
    return `${x}_${y}`;
  }

  private isCacheValid(tile: CachedTile): boolean {
    const age = Date.now() - tile.timestamp;
    return age < this.CACHE_EXPIRY_MS;
  }

  private async fetchTileData(x: number, y: number): Promise<CachedTile> {
    const bounds = ElevationService.tileToBounds(x, y);
    const points = `${bounds.swLat},${bounds.swLon},${bounds.neLat},${bounds.neLon}`;

    // Check request-level cache first
    const cachedRequest = this.requestCache.get(points);
    if (cachedRequest && this.isCacheValid(cachedRequest)) {
      return cachedRequest;
    }

    const pendingRequest = this.pendingApiRequests.get(points);
    if (pendingRequest) {
      return pendingRequest;
    }

    const fetchPromise = ElevationService.fetchApiData(points);
    this.pendingApiRequests.set(points, fetchPromise);

    try {
      const tile = await fetchPromise;
      this.requestCache.set(points, tile);
      return tile;
    } finally {
      this.pendingApiRequests.delete(points);
    }
  }

  private static async fetchApiData(points: string): Promise<CachedTile> {
    try {
      const response: crewTerrainCarpetListResponse = await crewTerrainCarpetList({ points });

      if (!response.data || !('data' in response.data)) {
        throw new Error(`API error! status: ${response.status}`);
      }

      const responseData = (response.data as CrewTerrainCarpetList200)?.data;
      if (!responseData?.carpet) {
        throw new Error('Invalid API response format: missing carpet data');
      }

      const { carpet, bounds: responseBounds } = responseData;
      const gridSizeLat = carpet.length;
      const gridSizeLon = carpet.length > 0 ? carpet[0].length : 0;

      if (!responseBounds?.sw || !responseBounds?.ne) {
        throw new Error('Invalid API response format: missing bounds');
      }

      if (responseBounds.sw.length < 2 || responseBounds.ne.length < 2) {
        throw new Error('Invalid API response format: bounds arrays must have at least 2 elements');
      }

      const tile: CachedTile = {
        bounds: {
          swLat: responseBounds.sw[0],
          swLon: responseBounds.sw[1],
          neLat: responseBounds.ne[0],
          neLon: responseBounds.ne[1]
        },
        carpet,
        gridSizeLat,
        gridSizeLon,
        timestamp: Date.now()
      };

      return tile;
    } catch (error) {
      console.error(`Failed to fetch elevation data for points ${points}:`, error);
      throw error;
    }
  }

  private async getTile(lat: number, lon: number): Promise<CachedTile | null> {
    const x = ElevationService.long2tileX(lon);
    const y = ElevationService.lat2tileY(lat);
    const tileKey = ElevationService.getTileKeyFromCoords(x, y);

    const cached = this.tileCache.get(tileKey);
    if (cached && this.isCacheValid(cached)) {
      return cached;
    }

    const pending = this.pendingRequests.get(tileKey);
    if (pending) {
      return pending;
    }

    const fetchPromise = this.fetchTileData(x, y);
    this.pendingRequests.set(tileKey, fetchPromise);

    try {
      const tile = await fetchPromise;
      this.tileCache.set(tileKey, tile);
      return tile;
    } catch (error) {
      return null;
    } finally {
      this.pendingRequests.delete(tileKey);
    }
  }

  async getElevation(lat: number, lon: number): Promise<number | null> {
    const tile = await this.getTile(lat, lon);
    if (!tile) {
      return null;
    }

    const { bounds, gridSizeLat, gridSizeLon, carpet } = tile;
    const { swLat, swLon, neLat, neLon } = bounds;

    const latDeltaSw = lat - swLat;
    const lonDeltaSw = lon - swLon;

    const cellSizeLat = (neLat - swLat) / gridSizeLat;
    const cellSizeLon = (neLon - swLon) / gridSizeLon;

    const latIndex = Math.floor(latDeltaSw / cellSizeLat);
    const lonIndex = Math.floor(lonDeltaSw / cellSizeLon);

    // Bounds checking
    if (latIndex < 0 || latIndex >= gridSizeLat || lonIndex < 0 || lonIndex >= gridSizeLon) {
      return null;
    }

    // Direct lookup from 2D array (nearest neighbor, no interpolation)
    // Note: carpet is indexed as [latIndex][lonIndex] where latIndex=0 is southernmost
    const elevation = carpet[latIndex]?.[lonIndex];

    // Validate elevation value
    if (elevation === null || elevation === undefined || Number.isNaN(elevation) || !Number.isFinite(elevation)) {
      return null;
    }

    return elevation;
  }

  /**
   * Preload tiles for a bounding box (useful for preloading visible map area)
   * Limits the number of tiles to prevent excessive requests
   */
  async preloadTiles(
    bounds: { sw: [number, number]; ne: [number, number] },
    maxTiles = 12,
    bufferTiles = 1
  ): Promise<void> {
    const swX = ElevationService.long2tileX(bounds.sw[1]);
    const swY = ElevationService.lat2tileY(bounds.sw[0]);
    const neX = ElevationService.long2tileX(bounds.ne[1]);
    const neY = ElevationService.lat2tileY(bounds.ne[0]);

    const promises: Promise<CachedTile | null>[] = [];
    let tileCount = 0;

    for (let x = swX - bufferTiles; x <= neX + bufferTiles && tileCount < maxTiles; x++) {
      for (let y = swY - bufferTiles; y <= neY + bufferTiles && tileCount < maxTiles; y++) {
        const tileKey = ElevationService.getTileKeyFromCoords(x, y);
        // Only preload if not cached and not pending
        if (!this.tileCache.has(tileKey) && !this.pendingRequests.has(tileKey)) {
          const tileBounds = ElevationService.tileToBounds(x, y);
          // Use center of tile for getTile lookup
          const centerLat = (tileBounds.swLat + tileBounds.neLat) / 2;
          const centerLon = (tileBounds.swLon + tileBounds.neLon) / 2;
          promises.push(this.getTile(centerLat, centerLon));
          tileCount++;
        }
      }
    }

    // Preload in parallel, but don't wait for all to complete
    if (promises.length > 0) {
      Promise.all(promises).catch((error) => {
        console.warn('Some tiles failed to preload:', error);
      });
    }
  }
}

export const elevationService = new ElevationService();

export type { TileBounds, CachedTile };
